import { Text, Tooltip } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { Util } from '@finley-technologies/finley-types';

import {
  IconTag,
  IconElementTag,
} from '~/packages/common-frontend/components/Tag';
import { useQueryParams } from '~/packages/common-frontend/hooks/useQueryParams';
import { Link } from '~/valleyBank/domains/components/Link';

export const CUSTOMER_URL_PARAM = 'arrangmentGroup';

export const generatArrangmentGroupQueryParam = (
  arrangmentGroupId: string,
): string => {
  return `?${CUSTOMER_URL_PARAM}=${arrangmentGroupId}`;
};

export const generateArrangementGroupUrl = ({
  path,
  arrangementGroupNumber,
}: {
  path: string;
  arrangementGroupNumber: string;
}): string => {
  return `${path}${generatArrangmentGroupQueryParam(arrangementGroupNumber)}`;
};

type ArrangementGroupLinkProps = {
  children: React.ReactNode;
  arrangementGroup: string;
  path: string;
  scrollToTop?: boolean;
};
const ArrangementGroupLink = ({
  children,
  arrangementGroup,
  path,
  scrollToTop = true,
}: ArrangementGroupLinkProps) => {
  return (
    <Tooltip label="Arrangement Group" placement="auto" textAlign="center">
      <span>
        <Link
          to={generateArrangementGroupUrl({
            path,
            arrangementGroupNumber: arrangementGroup,
          })}
          onClick={
            scrollToTop
              ? () => {
                  window.scrollTo(0, 0);
                }
              : undefined
          }
        >
          {children}
        </Link>
      </span>
    </Tooltip>
  );
};

type ArrangementGroupSummaryLinkProps = {
  children: React.ReactNode;
  arrangementGroup: string;
};
export const ArrangementGroupSummaryLink = ({
  children,
  arrangementGroup,
}: ArrangementGroupSummaryLinkProps) => (
  <ArrangementGroupLink
    arrangementGroup={arrangementGroup}
    path="/valley-bank/arrangement-groups"
  >
    {children}
  </ArrangementGroupLink>
);

export const NoArrangementGroupTag = () => {
  return <IconTag icon="folder-tree" text="No Arrangement Group Found" />;
};

type ArrangementGroupTagProps = {
  arrangmentGroup?: Util.Maybe<string>;
};
export const ArrangementGroupTag = ({
  arrangmentGroup,
}: ArrangementGroupTagProps) => {
  if (!arrangmentGroup) {
    return <NoArrangementGroupTag />;
  }
  return (
    <IconElementTag icon="folder-tree">
      <ArrangementGroupSummaryLink arrangementGroup={arrangmentGroup}>
        <Text fontWeight="bold">{arrangmentGroup}</Text>
      </ArrangementGroupSummaryLink>
    </IconElementTag>
  );
};

export const useArrangmentGroupUrlParam = (): {
  arrangementGroupNumber: string | null;
  setArrangementGroupNumber: (newLoanAccountId: string | null) => void;
  generateCurrentArrangmentGroupUrl: (path: string) => string;
} => {
  const navigate = useNavigate();
  const { pathname: currentPath } = useLocation();
  const query = useQueryParams();
  const arrangementGroupNumber = query.get(CUSTOMER_URL_PARAM);

  const setArrangementGroupNumber = useCallback(
    (newLoanAccountId: string | null) => {
      // @jendevelops TODO: add error message when loan account id doesn't exist
      if (newLoanAccountId) {
        navigate(
          generateArrangementGroupUrl({
            path: currentPath,
            arrangementGroupNumber: newLoanAccountId,
          }),
        );
      }
    },
    [currentPath, navigate],
  );

  const _generateCurrentArrangmentGroupUrl = useCallback(
    (path: string) =>
      generateArrangementGroupUrl({
        path,
        arrangementGroupNumber: arrangementGroupNumber || '',
      }),
    [arrangementGroupNumber],
  );

  return {
    arrangementGroupNumber,
    setArrangementGroupNumber,
    generateCurrentArrangmentGroupUrl: _generateCurrentArrangmentGroupUrl,
  };
};
